import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Copy, Lock, X } from 'lucide-react';
import {
  bootTokensApi,
  meApi,
  type BootTokenCreated,
  type BootTokenCreateRequest,
} from '@/api/client';
import { useBootEvents, type BootStreamEvent } from '@/hooks/useBootEvents';
import { useFeature } from '@/hooks/useFeature';

export interface AddNodeWizardProps {
  open: boolean;
  siteId: string;
  onClose: () => void;
  /** Phase 5d — when set, the wizard skips the Configure step and
   *  jumps straight to the URL + Live progress display. Used by the
   *  pairing flow: the operator paired a waiting server via
   *  PairWaitingServerModal, which mints a token on the backend and
   *  hands it here to render the post-pair timeline. */
  preMintedToken?: BootTokenCreated | null;
}

interface TimelineEntry {
  ts: string;
  stage: string;
  outcome: string;
  reason: string | null;
  clientIp: string | null;
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.max(0, Math.floor(ms / 1000))}s ago`;
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / 3600_000)}h ago`;
}

/**
 * Phase 5a — Add Node wizard. Right-edge slide-in drawer. State-driven:
 *
 *   Step 1 (Configure): TTL + requireApproval + optional MAC/UUID pin.
 *   Step 2 (Show URL):  Display the minted per-token boot URL.
 *   Step 3 (Live):      Subscribe to boot-service SSE, render a timeline
 *                       of boot_attempts rows scoped to the minted token.
 *
 * Slug resolution mirrors PendingBoots.tsx: fetch the logged-in org by
 * ID, use `slug` for both the `POST /tokens` and `EventSource(/events)`
 * URLs.
 *
 * Netboot only: it mints per-node boot URLs. (A box added from the USB image
 * needs no wizard: it appears in Nodes once it connects.) Behind the
 * `netboot` feature flag, off by default (#954): it never opens, and never
 * mints, while the flag is off for this site.
 */
export default function AddNodeWizard(props: AddNodeWizardProps): JSX.Element | null {
  const netboot = useFeature('netboot', { siteId: props.siteId });
  if (!netboot) return null;
  return <AddNodeWizardBody {...props} />;
}

function AddNodeWizardBody({ open, siteId, onClose, preMintedToken }: AddNodeWizardProps): JSX.Element | null {
  const [ttlMinutes, setTtlMinutes] = useState(30);
  const [requireApproval, setRequireApproval] = useState(false);
  const [allowedMac, setAllowedMac] = useState('');
  const [allowedUuid, setAllowedUuid] = useState('');
  const [token, setToken] = useState<BootTokenCreated | null>(preMintedToken ?? null);
  const [copied, setCopied] = useState(false);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [approvalCode, setApprovalCode] = useState<string | null>(null);
  const [approvalState, setApprovalState] = useState<'requested' | 'approved' | 'denied' | null>(null);

  // Reset transient state every time the drawer is reopened — we don't
  // surface "previous mint history", the audit log is the source of truth.
  // When `preMintedToken` is supplied (Phase 5d pair flow), keep it as
  // the initial token so the wizard renders the URL + Live progress
  // directly, skipping Configure.
  useEffect(() => {
    if (!open) return;
    setTtlMinutes(30);
    setRequireApproval(false);
    setAllowedMac('');
    setAllowedUuid('');
    setToken(preMintedToken ?? null);
    setCopied(false);
    setTimeline([]);
    setApprovalCode(null);
    setApprovalState(null);
  }, [open, preMintedToken]);

  const { data: org } = useQuery({
    queryKey: ['me', 'organization'],
    queryFn: () => meApi.organization(),
    enabled: open,
    staleTime: 60_000,
  });
  const slug = org?.slug;

  const mintMutation = useMutation({
    mutationFn: async (body: BootTokenCreateRequest) => {
      if (!slug) throw new Error('Org slug not loaded yet');
      return bootTokensApi.create(slug, body);
    },
    onSuccess: (data) => {
      setToken(data);
    },
  });

  // SSE subscription — only when we have a slug AND a minted token to scope
  // the timeline. The hook filters server-side by siteId; we additionally
  // filter in-memory by bootTokenId since approval_change events come with
  // siteId=null (the SSE pass-through rule) and the wizard timeline cares
  // only about ITS token.
  const onEvent = useCallback((evt: BootStreamEvent) => {
    if (!token) return;
    if (evt.kind === 'boot_attempt') {
      if (evt.bootTokenId !== token.id) return;
      setTimeline((prev) => [
        ...prev,
        {
          ts: evt.createdAt,
          stage: evt.stage,
          outcome: evt.outcome,
          reason: evt.reason,
          clientIp: evt.clientIp,
        },
      ]);
    } else if (evt.kind === 'approval_change') {
      if (evt.bootTokenId !== token.id) return;
      setApprovalCode(evt.code);
      setApprovalState(evt.state);
    }
  }, [token]);

  useBootEvents({
    slug,
    siteId,
    enabled: open && !!token,
    onEvent,
  });

  const handleMint = () => {
    const body: BootTokenCreateRequest = {
      siteId,
      ttlSeconds: ttlMinutes * 60,
      requireApproval,
    };
    if (allowedMac.trim()) body.allowedMac = allowedMac.trim();
    if (allowedUuid.trim()) body.allowedUuid = allowedUuid.trim();
    mintMutation.mutate(body);
  };

  const handleCopy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token.bootUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const stageLabel = useMemo<Record<string, string>>(
    () => ({
      ipxe: 'iPXE bootloader fetched',
      kernel: 'Kernel served',
      initrd: 'Initramfs served',
      'kernel.sig': 'Kernel signature served',
      'initrd.sig': 'Initramfs signature served',
      poll: 'Polling for approval',
      efi: 'iPXE EFI binary served',
      denied: 'Boot denied',
    }),
    [],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-surface border-l border-surface-elevated shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h2 className="text-base font-semibold text-silver-bright">
            {preMintedToken ? 'Paired — booting' : 'Add node'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-silver-muted hover:text-silver-bright transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body — scrolls if content exceeds viewport */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {!token && (
            <>
              <p className="text-xs text-silver-muted">
                Mint a one-shot boot URL for this site. Hand the URL to your
                BIOS HTTP Boot setting or fleet automation; the timeline
                below ticks live as the node fetches the kernel.
              </p>

              <div className="space-y-1.5">
                <label className="block text-xs text-silver-muted">Token lifetime</label>
                <select
                  value={ttlMinutes}
                  onChange={(e) => setTtlMinutes(parseInt(e.target.value, 10))}
                  className="w-full bg-surface-deep border border-surface-elevated rounded px-3 py-2 text-sm text-silver-bright focus:border-gold focus:outline-none"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={240}>4 hours</option>
                  <option value={1440}>24 hours</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="flex items-start gap-2 text-sm text-silver-bright cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requireApproval}
                    onChange={(e) => setRequireApproval(e.target.checked)}
                    className="mt-0.5 accent-gold"
                  />
                  <span className="flex-1">
                    Require operator approval before booting
                    <span className="block text-xs text-silver-muted mt-0.5">
                      BIOS will display a 6-character code; approve it in the
                      Pending Boots section of this site.
                    </span>
                  </span>
                </label>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs text-silver-muted">Pin to MAC (optional)</label>
                <input
                  type="text"
                  value={allowedMac}
                  onChange={(e) => setAllowedMac(e.target.value)}
                  placeholder="aa:bb:cc:dd:ee:ff"
                  className="w-full bg-surface-deep border border-surface-elevated rounded px-3 py-2 text-sm font-mono text-silver-bright placeholder:text-silver-muted/40 focus:border-gold focus:outline-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs text-silver-muted">Pin to system UUID (optional)</label>
                <input
                  type="text"
                  value={allowedUuid}
                  onChange={(e) => setAllowedUuid(e.target.value)}
                  placeholder="System UUID from BIOS"
                  className="w-full bg-surface-deep border border-surface-elevated rounded px-3 py-2 text-sm font-mono text-silver-bright placeholder:text-silver-muted/40 focus:border-gold focus:outline-none"
                />
              </div>

              {mintMutation.error && (
                <div className="rounded-lg border border-status-error/30 bg-status-error/5 px-3 py-2 text-sm text-status-error">
                  Mint failed: {(mintMutation.error as Error).message}
                </div>
              )}
            </>
          )}

          {token && (
            <>
              <div className="space-y-2">
                <div className="text-xs text-silver-muted">
                  Per-token boot URL — expires {new Date(token.expiresAt).toLocaleString()}.
                </div>
                <div className="bg-surface-deep/50 border border-surface-elevated rounded-lg px-3 py-2 font-mono text-xs text-gold break-all">
                  {token.bootUrl}
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 text-xs text-silver-muted hover:text-silver-bright transition-colors"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied' : 'Copy URL'}
                </button>
              </div>

              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-silver-muted">
                  Live progress
                </h3>

                {approvalCode && approvalState === 'requested' && (
                  <div className="rounded-lg border border-status-warning/30 bg-status-warning/5 px-3 py-2 text-sm text-status-warning flex items-start gap-2">
                    <Lock size={14} className="mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      Boot waiting on approval — code{' '}
                      <span className="font-mono font-semibold">{approvalCode}</span>.
                      Close this dialog and approve it in the Pending Boots
                      section below.
                    </div>
                  </div>
                )}
                {approvalCode && approvalState === 'approved' && (
                  <div className="rounded-lg border border-status-healthy/30 bg-status-healthy/5 px-3 py-2 text-sm text-status-healthy">
                    Approval granted for code{' '}
                    <span className="font-mono">{approvalCode}</span>.
                  </div>
                )}
                {approvalCode && approvalState === 'denied' && (
                  <div className="rounded-lg border border-status-error/30 bg-status-error/5 px-3 py-2 text-sm text-status-error">
                    Boot denied (code{' '}
                    <span className="font-mono">{approvalCode}</span>).
                  </div>
                )}

                {timeline.length === 0 ? (
                  <p className="text-xs text-silver-muted py-2">
                    Waiting for the new server to fetch iPXE…
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {timeline.map((entry, i) => (
                      <li
                        key={`${entry.ts}-${i}`}
                        className="bg-surface-deep/50 border border-surface-elevated rounded-lg px-3 py-2 text-sm"
                      >
                        <div className="text-silver-bright">
                          {stageLabel[entry.stage] ?? `Stage: ${entry.stage}`}
                        </div>
                        <div className="text-xs text-silver-muted mt-0.5">
                          {entry.outcome}
                          {entry.reason ? ` · ${entry.reason}` : ''}
                          {entry.clientIp ? ` · ${entry.clientIp}` : ''}
                          {' · '}
                          {relTime(entry.ts)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          {!token ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMint}
                disabled={!slug || mintMutation.isPending}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-gold text-surface-deep hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mintMutation.isPending ? 'Minting…' : 'Mint boot URL'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-gold text-surface-deep hover:bg-gold-light transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
